import React, { createContext, useContext, ReactNode } from "react";
import { AuthContextType } from "./types";
import { useAuth as useAuthHook } from "./useAuth";

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Props for the AuthProvider component
 */
interface AuthProviderProps {
  /** Child components that will have access to the authentication context */
  children: ReactNode;
}

/**
 * Authentication provider component
 *
 * Wraps the application to provide authentication context to all child components.
 * Uses the useAuth hook internally to manage authentication state.
 *
 * @param props - Component props, see AuthProviderProps
 * @returns Provider component that supplies authentication context
 *
 * @example
 * ```tsx
 * function App() {
 *   return (
 *     <AuthProvider>
 *       <Stack>
 *         <Stack.Screen name="login" component={LoginScreen} />
 *         <Stack.Screen name="chat" component={ChatScreen} />
 *       </Stack>
 *     </AuthProvider>
 *   );
 * }
 * ```
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const auth = useAuthHook();

  return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
}

/**
 * Hook to access authentication context
 *
 * Provides access to the authentication state and functions from anywhere
 * within the AuthProvider component tree.
 *
 * @returns AuthContextType containing user state and auth functions
 * @throws Error if used outside of AuthProvider
 *
 * @example
 * ```tsx
 * function LoginScreen() {
 *   const { user, isLoading, signInWithGoogle } = useAuth();
 *
 *   if (isLoading) return <LoadingSpinner />;
 *   if (user) return <Redirect to="/chat" />;
 *
 *   return <Button onPress={signInWithGoogle}>Sign In</Button>;
 * }
 * ```
 */
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
